import React, { useMemo } from 'react';
import cn from 'clsx';
import { Layout, ColorValueStrict } from 'fronton-react';
import { SectionTitle } from '../section-title';
import { Typography } from '../typography';
import { CarouselContent } from './carousel-content';
import { PictureContent, PictureContentProps } from './picture-content';
import {
  HeaderSectionText,
  StyledSection,
  StylesSectionProps,
  headerSectionContainer,
  headerSectionImage,
  headerSectionTextContainer,
  headerSectionTitle,
  headerSectionDescription,
  headerSectionPatternDesktop,
  headerSectionPatternTablet,
  headerSectionPatternMobile,
  headerSectionButton,
} from './header-section-styles';
import { ButtonWithGAClickButton } from '../button';

export type HeaderImageStructure = {
  srcSet?: never;
} & PictureContentProps;

export type HeaderGifStructure = {
  ldSrcSet?: never;
  dSrcSet?: never;
  tSrcSet?: never;
  mSrcSet?: never;
  srcSet: string;
  alt: string;
  title: string;
};

export type HeaderCarouselContent = {
  img?: never;
  imgList: HeaderImageStructure[];
  mediaType: 'carousel';
};

export type HeaderImageContent = {
  img: HeaderImageStructure | HeaderGifStructure;
  imgList?: never;
  mediaType: 'image' | 'gif';
};

export type HeaderCustomContent = {
  title?: never;
  description?: never;
  headerContent: React.ReactNode;
}

export type HeaderContent = {
  title: string;
  description: string;
  headerContent?: never;
}

export type HeaderSectionProps = {
  textColor?: ColorValueStrict;
  headerContent?: React.ReactNode;
  containerClassname?: string;
  hasPattern?: boolean;
  buttonTitle?: string;
  elementBlock?: string;
  elementText?: string;
  handleHeaderButtonClick?: () => void;
} & (HeaderCarouselContent | HeaderImageContent) &
  (HeaderCustomContent | HeaderContent) &
  StylesSectionProps;

export const HeaderSection: React.FC<HeaderSectionProps> = ({
  bgColor,
  textColor = 'text-invert',
  description,
  headerContent,
  img,
  imgList,
  title,
  mediaType,
  containerClassname,
  hasPattern = true,
  buttonTitle,
  elementBlock,
  elementText,
  handleHeaderButtonClick,
}) => {
  const MediaContent = useMemo(() => {
    if (mediaType === 'carousel' && imgList?.length) {
      return <CarouselContent carouselList={imgList} />;
    }

    if (mediaType === 'gif') {
      const { alt, title: gifTitle, srcSet } = img as HeaderGifStructure;
      return <img alt={alt} className={headerSectionImage} src={srcSet} title={gifTitle} />;
    }

    return <PictureContent {...(img as HeaderImageStructure)} />;
  }, [mediaType, img, imgList]);

  return (
    <StyledSection bgColor={bgColor} data-testid="header">
      <Layout className={cn(headerSectionContainer, containerClassname)}>
        <Layout className={headerSectionTextContainer}>
          {headerContent
            ? headerContent
            : (
              <HeaderSectionText>
                <SectionTitle as="h1" className={headerSectionTitle} color={textColor}>
                  {title}
                </SectionTitle>
                <Typography className={headerSectionDescription} color={textColor} variant="l" size="body_short">
                  {description}
                </Typography>
                {buttonTitle && (
                  <ButtonWithGAClickButton
                    className={headerSectionButton}
                    aria-label={buttonTitle}
                    elementBlock={elementBlock || ''}
                    elementName={buttonTitle || ''}
                    elementText={elementText || ''}
                    onClick={handleHeaderButtonClick}
                    adaptive={false}
                    title="Записаться на консультацию">
                    {buttonTitle}
                  </ButtonWithGAClickButton>
                )}
              </HeaderSectionText>
            )
          }
        </Layout>
        {MediaContent}
        {hasPattern && (
          <>
            <svg
              className={headerSectionPatternDesktop}
              width="768"
              height="480"
              viewBox="0 0 768 480"
              fill="none">
              <path d="M768 479.941H606V337.745L768 186.862V479.941Z" fill={bgColor} />
              <path d="M100.319 0L0 99.263V0L100.319 0Z" fill={bgColor} />
            </svg>
            <svg className={headerSectionPatternTablet} viewBox="0 0 768 480" fill="none">
              <path d="M768 480H631V419.112L768 292.604V480Z" fill={bgColor} />
              <path d="M252.885 0L54 186.099V480H0V0L252.885 0Z" fill={bgColor} />
            </svg>
            <svg className={headerSectionPatternMobile} viewBox="0 0 320 256" fill="none">
              <path d="M320 324H259.25V213.516L320 157.192V324Z" fill={bgColor} />
              <path d="M136.608 0L16 115.214V256H-0.25001V0H136.608Z" fill={bgColor} />
            </svg>
          </>
        )}
      </Layout>
    </StyledSection>
  );
};

HeaderSection.defaultProps = {
  textColor: 'text-invert',
  containerClassname: '',
  buttonTitle: '',
  elementBlock: '',
  elementText: '',
};
